import { Object as FabricObject } from 'fabric/fabric-impl';
import mediaManagerTemplateDataMapper from 'modules/core/domain/data-mappers/media-manager-template-data-mapper';
import mediaManagerTemplateGroupDataMapper from 'modules/manager/domain/data-mappers/media-manager-template-group-data-mapper';
import {
  CopyMediaManagerTemplate,
  CreateMediaManagerTemplate,
  MediaManagerTemplateFilter,
  UpdateMediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/actions';
import {
  MediaManagerRenderedImage,
  MediaManagerTemplate,
} from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import graphqlResource from 'modules/core/graphql-resource';
import {
  COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  COPY_MEDIA_MANAGER_TEMPLATE_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  MEDIA_MANAGER_PRESETS_QUERY,
  MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATES_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY,
  MEDIA_MANAGER_TEMPLATE_GROUP_QUERY,
  MEDIA_MANAGER_TEMPLATE_QUERY,
  MediaManagerTemplateGql,
  MediaManagerTemplateGroupGql,
  UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION,
  UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION,
  unwrapGraphqlData,
} from 'modules/manager/domain/graphql';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { MatchLayoutSettings } from 'modules/manager/domain/match-layout';
import { PlayerRankingSettings } from 'modules/manager/domain/player-stats';
import supabase from 'modules/core/supabase/supabase';

export interface UpdateTemplateObjectsInput {
  id: number;
  objects: FabricObject[];
  version: string;
}

export interface UpdateTemplateMatchLayoutInput {
  id: number;
  matchLayout: MatchLayoutSettings;
}

export interface UpdateTemplatePlayerRankingInput {
  id: number;
  playerRanking: PlayerRankingSettings;
}

export interface PersistRenderedImageInput {
  matchId: number;
  templateId: number;
  dataUrl: string;
}

export interface StageRenderedImageInput {
  /** '' for a non-GROUP stage's single table, team-group id (as text) for a GROUP stage. */
  groupKey: string;
  groupId?: number;
  groupName?: string;
  dataUrl: string;
}

export interface PersistStageRenderedImagesInput {
  stageId: number;
  templateId: number;
  images: StageRenderedImageInput[];
}

export interface PersistChampionshipRenderedImageInput {
  championshipId: number;
  templateId: number;
  dataUrl: string;
  /** Defaults to '' — one image per template. Reserved for a future per-team breakdown. */
  groupKey?: string;
}

export class MediaManagerRepository {
  async getTemplates(filter?: MediaManagerTemplateFilter): Promise<MediaManagerTemplate[]> {
    if (filter?.id !== undefined) {
      return [await this.getTemplateById(filter.id)];
    }
    if (filter?.tournamentId !== undefined) {
      const envelope = await graphqlResource.query<{
        data: { mediaManagerTemplates: MediaManagerTemplateGql[] };
        errors?: { message: string; extensions?: { code?: number } }[];
      }>(MEDIA_MANAGER_TEMPLATES_QUERY, {
        tournamentId: filter.tournamentId,
        sportType: filter.sportType,
        templateGroupId: filter.templateGroupId,
      });
      const data = unwrapGraphqlData(envelope.data);
      return data.mediaManagerTemplates.map(mediaManagerTemplateDataMapper);
    }
    const envelope = await graphqlResource.query<{
      data: { mediaManagerPresets: MediaManagerTemplateGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_PRESETS_QUERY, { sportType: filter?.sportType });
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerPresets.map(mediaManagerTemplateDataMapper);
  }

  async getTemplateById(id: number): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_QUERY, { id });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.mediaManagerTemplate);
  }

  async createTemplate(input: CreateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { createMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(CREATE_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.createMediaManagerTemplate);
  }

  async updateTemplate(input: UpdateMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { updateMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(UPDATE_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.updateMediaManagerTemplate);
  }

  async deleteTemplate(id: number): Promise<void> {
    const envelope = await graphqlResource.query<{
      data: { deleteMediaManagerTemplate: { status: string } };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(DELETE_MEDIA_MANAGER_TEMPLATE_MUTATION, { id });
    unwrapGraphqlData(envelope.data);
  }

  async copyTemplate(input: CopyMediaManagerTemplate): Promise<MediaManagerTemplate> {
    const envelope = await graphqlResource.query<{
      data: { copyMediaManagerTemplate: MediaManagerTemplateGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(COPY_MEDIA_MANAGER_TEMPLATE_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateDataMapper(data.copyMediaManagerTemplate);
  }

  async getTemplateGroups(tournamentId: number): Promise<MediaManagerTemplateGroup[]> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplateGroups: MediaManagerTemplateGroupGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_GROUPS_QUERY, { tournamentId });
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerTemplateGroups.map(mediaManagerTemplateGroupDataMapper);
  }

  async getPresetTemplateGroups(): Promise<MediaManagerTemplateGroup[]> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerPresetTemplateGroups: MediaManagerTemplateGroupGql[] };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_PRESET_TEMPLATE_GROUPS_QUERY);
    const data = unwrapGraphqlData(envelope.data);
    return data.mediaManagerPresetTemplateGroups.map(mediaManagerTemplateGroupDataMapper);
  }

  async getTemplateGroupById(id: number): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { mediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(MEDIA_MANAGER_TEMPLATE_GROUP_QUERY, { id });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.mediaManagerTemplateGroup);
  }

  async createTemplateGroup(input: { name: string; tournamentId?: number | null }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { createMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(CREATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.createMediaManagerTemplateGroup);
  }

  async updateTemplateGroup(input: { id: number; name?: string }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { updateMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(UPDATE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.updateMediaManagerTemplateGroup);
  }

  async deleteTemplateGroup(id: number, deleteTemplates = false): Promise<void> {
    const envelope = await graphqlResource.query<{
      data: { deleteMediaManagerTemplateGroup: { status: string } };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(DELETE_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { id, deleteTemplates });
    unwrapGraphqlData(envelope.data);
  }

  async copyTemplateGroup(input: {
    templateGroupId: number;
    tournamentId: number;
    name?: string;
  }): Promise<MediaManagerTemplateGroup> {
    const envelope = await graphqlResource.query<{
      data: { copyMediaManagerTemplateGroup: MediaManagerTemplateGroupGql };
      errors?: { message: string; extensions?: { code?: number } }[];
    }>(COPY_MEDIA_MANAGER_TEMPLATE_GROUP_MUTATION, { input });
    const data = unwrapGraphqlData(envelope.data);
    return mediaManagerTemplateGroupDataMapper(data.copyMediaManagerTemplateGroup);
  }

  updateTemplateObjects(input: UpdateTemplateObjectsInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      objects: input.objects,
      version: input.version,
    });
  }

  updateTemplateMatchLayout(input: UpdateTemplateMatchLayoutInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      matchLayout: input.matchLayout,
    });
  }

  updateTemplatePlayerRanking(input: UpdateTemplatePlayerRankingInput): Promise<MediaManagerTemplate> {
    return this.updateTemplate({
      id: input.id,
      playerRanking: input.playerRanking,
    });
  }

  /**
   * Shared "replace the one image for this target" flow: upload → upsert → roll
   * back the uploaded file if the DB write fails → drop the superseded file.
   *
   * Used by `persistRenderedImage` (match) and `persistChampionshipRenderedImage`
   * (player statistics). `persistStageRenderedImages` deliberately does not use
   * it — it fans out per group and additionally reconciles stale rows.
   *
   * Both cleanup paths swallow their errors: an orphaned file is a storage-cost
   * problem, while rethrowing here would turn a successful render into a
   * reported failure.
   */
  private async replaceSingleImage(
    previousImageUrl: string | null,
    upload: () => Promise<{ publicUrl: string }>,
    upsert: (publicUrl: string) => Promise<MediaManagerRenderedImage>,
  ): Promise<MediaManagerRenderedImage> {
    const { publicUrl } = await upload();

    let result: MediaManagerRenderedImage;
    try {
      result = await upsert(publicUrl);
    } catch (err) {
      await supabase.renderedImagesStorage.remove(publicUrl).catch((cleanupErr) => {
        console.error('[render] storage rollback failed', cleanupErr);
      });
      throw err;
    }

    if (previousImageUrl && previousImageUrl !== publicUrl) {
      await supabase.renderedImagesStorage.remove(previousImageUrl).catch((cleanupErr) => {
        console.error('[render] previous image cleanup failed', cleanupErr);
      });
    }

    return result;
  }

  async persistRenderedImage(input: PersistRenderedImageInput): Promise<MediaManagerRenderedImage> {
    const previous = await supabase.mediaManagerRenderedImages.getByMatchAndTemplate(
      input.matchId,
      input.templateId,
    );

    return this.replaceSingleImage(
      previous?.imageUrl ?? null,
      () => supabase.renderedImagesStorage.upload(input.matchId, input.templateId, input.dataUrl),
      (imageUrl) =>
        supabase.mediaManagerRenderedImages.upsert({
          matchId: input.matchId,
          templateId: input.templateId,
          imageUrl,
        }),
    );
  }

  /**
   * Persists the single image a player-statistics template produces for a
   * championship. Unlike the standings renderer there is no fan-out and no
   * stale-group reconciliation — one championship + one template = one image.
   */
  async persistChampionshipRenderedImage(
    input: PersistChampionshipRenderedImageInput,
  ): Promise<MediaManagerRenderedImage> {
    const groupKey = input.groupKey ?? '';
    const previous = await supabase.mediaManagerRenderedImages.getByChampionshipAndTemplate(
      input.championshipId,
      input.templateId,
      groupKey,
    );

    return this.replaceSingleImage(
      previous?.imageUrl ?? null,
      () =>
        supabase.renderedImagesStorage.uploadChampionship(
          input.championshipId,
          input.templateId,
          groupKey,
          input.dataUrl,
        ),
      (imageUrl) =>
        supabase.mediaManagerRenderedImages.upsertChampionship({
          championshipId: input.championshipId,
          templateId: input.templateId,
          groupKey,
          imageUrl,
        }),
    );
  }

  async deleteRenderedImage(id: string): Promise<void> {
    const row = await supabase.mediaManagerRenderedImages.getById(id);
    if (!row) return;

    await supabase.renderedImagesStorage.remove(row.imageUrl);
    await supabase.mediaManagerRenderedImages.deleteById(id);
  }

  /**
   * Persists one image per standings target (one per team group for a GROUP
   * stage, one image for any other stage type) and reconciles the stored set
   * against the current backend groups: stale rows (groups that disappeared
   * since the last render) are removed, so re-rendering a stage always
   * leaves storage matching its current groups exactly.
   */
  async persistStageRenderedImages(
    input: PersistStageRenderedImagesInput,
  ): Promise<MediaManagerRenderedImage[]> {
    const existing = await supabase.mediaManagerRenderedImages.getByStageAndTemplate(
      input.stageId,
      input.templateId,
    );
    const existingByGroupKey = new Map(existing.map((row) => [row.groupKey, row]));
    const newGroupKeys = new Set(input.images.map((img) => img.groupKey));

    const results: MediaManagerRenderedImage[] = [];
    for (const img of input.images) {
      const previous = existingByGroupKey.get(img.groupKey);
      const { publicUrl } = await supabase.renderedImagesStorage.uploadStanding(
        input.stageId,
        input.templateId,
        img.groupKey,
        img.dataUrl,
      );

      let result: MediaManagerRenderedImage;
      try {
        result = await supabase.mediaManagerRenderedImages.upsertStage({
          stageId: input.stageId,
          templateId: input.templateId,
          groupKey: img.groupKey,
          groupId: img.groupId,
          groupName: img.groupName,
          imageUrl: publicUrl,
        });
      } catch (err) {
        await supabase.renderedImagesStorage.remove(publicUrl).catch((cleanupErr) => {
          console.error('[render] standings storage rollback failed', cleanupErr);
        });
        throw err;
      }

      if (previous && previous.imageUrl !== publicUrl) {
        await supabase.renderedImagesStorage.remove(previous.imageUrl).catch((cleanupErr) => {
          console.error('[render] previous standings image cleanup failed', cleanupErr);
        });
      }

      results.push(result);
    }

    const stale = existing.filter((row) => !newGroupKeys.has(row.groupKey));
    for (const row of stale) {
      await supabase.renderedImagesStorage.remove(row.imageUrl).catch((cleanupErr) => {
        console.error('[render] stale standings image cleanup failed', cleanupErr);
      });
      await supabase.mediaManagerRenderedImages.deleteById(row.id);
    }

    return results;
  }
}

const mediaManagerRepository = new MediaManagerRepository();
export default mediaManagerRepository;
